import { jsx } from "slate-hyperscript";
import { Mention, StylesFormatting } from "../slate-types";
import { CSSProperties } from "react";
import { Element as SlateElement } from "slate";

const getStyleAttributes = (el: HTMLElement): StylesFormatting => ({
  align: el.style.textAlign as CSSProperties["textAlign"],
  fontSize: getFontSize(el),
  fontFamily: el.style.fontFamily,
  color: el.style.color,
  background: el.style.backgroundColor || el.style.background,
  start: el.getAttribute("start"),
  listType: el.getAttribute("type"),
  margin: el.style.margin,
  marginLeft: el.style.marginLeft,
  width: el.style.width,
});

const getFontSize = (el: HTMLElement): string =>
  el.style.fontSize?.toLowerCase().endsWith("px") ? el.style.fontSize : undefined;

const ELEMENT_TAGS = {
  A: (el) => ({
    type: "link",
    url: el.getAttribute("href"),
    fontSize: getFontSize(el),
    fontFamily: el.style.fontFamily,
    background: el.style.background,
  }),
  BLOCKQUOTE: (el) => ({ type: "quote", ...getStyleAttributes(el) }),
  H1: (el) => ({ type: "h1", ...getStyleAttributes(el) }),
  H2: (el) => ({ type: "h2", ...getStyleAttributes(el) }),
  H3: (el) => ({ type: "h3", ...getStyleAttributes(el) }),
  H4: (el) => ({ type: "h4", ...getStyleAttributes(el) }),
  H5: (el) => ({ type: "h5", ...getStyleAttributes(el) }),
  H6: (el) => ({ type: "h6", ...getStyleAttributes(el) }),
  IMG: (el: HTMLImageElement) => ({
    type: "img",
    src: el.getAttribute("src"),
    alt: el.getAttribute("alt"),
    ...getStyleAttributes(el),
  }),
  LI: (el: HTMLLIElement) => ({ type: "li", ...getStyleAttributes(el) }),
  OL: (el: HTMLOListElement) => ({ type: "ol", ...getStyleAttributes(el) }),
  P: (el: HTMLParagraphElement) => ({ type: "paragraph", ...getStyleAttributes(el) }),
  PRE: () => ({ type: "code" }),
  UL: (el: HTMLUListElement) => ({ type: "ul", ...getStyleAttributes(el) }),
  MENTION: (value): Mention => ({
    type: "mention",
    id: value.replace("@", ""),
    label: value.replace("@", ""),
    children: [{ text: "" }],
  }),
};
// COMPAT: `B` is omitted here because Google Docs uses `<b>` in weird ways.
const TEXT_TAGS = {
  CODE: (el) => ({ code: true, fontSize: getFontSize(el), fontFamily: el.style.fontFamily }),
  DEL: (el) => ({ strikethrough: true, fontSize: getFontSize(el), fontFamily: el.style.fontFamily }),
  EM: (el) => ({ italic: true, fontSize: getFontSize(el), fontFamily: el.style.fontFamily }),
  I: (el) => ({ italic: true, fontSize: getFontSize(el), fontFamily: el.style.fontFamily }),
  S: (el) => ({ strikethrough: true, fontSize: getFontSize(el), fontFamily: el.style.fontFamily }),
  B: (el) => ({ bold: true, fontSize: getFontSize(el), fontFamily: el.style.fontFamily }),
  STRONG: (el) => ({ bold: true, fontSize: getFontSize(el), fontFamily: el.style.fontFamily }),
  U: (el) => ({ underline: true, fontSize: getFontSize(el), fontFamily: el.style.fontFamily }),
  SPAN: (el) => ({
    fontSize: getFontSize(el),
    color: el.style.color,
    fontFamily: el.style.fontFamily,
    background: el.style.background || el.style.backgroundColor,
  }),
};

export const deserialize = (el, trimLineBreaks?: boolean) => {
  if (trimLineBreaks && el.nodeName === "BODY") {
    // this code removes the \n and excess spaces when pasting html from Outlook
    el.innerHTML = el.innerHTML.replace(/[\n ]+/g, " ");
  }
  if (el.nodeType === Node.TEXT_NODE) {
    if (el.textContent?.trim() === "") {
      return null;
    }
    return jsx("text", {}, el.textContent);
  } else if (el.nodeType !== Node.ELEMENT_NODE) {
    return null;
  } else if (el.nodeName === "BR") {
    return "\n";
  }
  const { nodeName } = el;
  let parent = el;

  if (nodeName === "PRE" && el.childNodes[0] && el.childNodes[0].nodeName === "CODE") {
    parent = el.childNodes[0] as HTMLElement;
  }
  let children = Array.from(parent.childNodes)
    .map((node) => deserialize(node))
    .flat();
  if (children.length === 0 || (children.length === 1 && children[0] === null)) {
    children = [{ text: "" }];
  }

  if (el.nodeName === "BODY") {
    return jsx("fragment", {}, children);
  }
  // check if Mention
  if (el.nodeName === "SPAN" && el.textContent.startsWith("@")) {
    const attrs = ELEMENT_TAGS.MENTION(el.textContent);
    return jsx("element", attrs, children);
  } else if (ELEMENT_TAGS[nodeName]) {
    const attrs = ELEMENT_TAGS[nodeName](el);
    return jsx("element", attrs, children);
  }

  if (TEXT_TAGS[nodeName]) {
    const attrs = TEXT_TAGS[nodeName](el);

    // Handle nested children structure
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const processNestedChild = (nestedChild: any): any => {
      // If it's a string, create a text node
      if (typeof nestedChild === "string") {
        return jsx("text", attrs, { text: nestedChild });
      }

      // If it has only children property and one child, process that child
      if (!nestedChild.type && !nestedChild.text && nestedChild.children?.length === 1) {
        return processNestedChild(nestedChild.children[0]);
      }

      // If it's an element, preserve its structure
      if (SlateElement.isElement(nestedChild)) {
        return jsx("element", { ...attrs, ...nestedChild }, nestedChild.children);
      }

      // For text nodes, merge attributes
      return jsx("text", { ...attrs, ...nestedChild }, nestedChild);
    };
    return children.filter(Boolean).map((child) => {
      return processNestedChild(child);
    });
  }

  function manageEmptyChildren(children) {
    return children.map((child) => {
      if (!child || !child.children) {
        return child;
      }
      if (child.children.length === 0) {
        return { ...child, children: [{ text: "" }] };
      } else if (child.children) {
        return { ...child, children: manageEmptyChildren(child.children) };
      }
      return child;
    });
  }

  const normalizedChildren = children
    // .filter((i) => i && typeof i !== "string" && i.type)
    .map((item) => {
      if (typeof item === "string") {
        return { text: item };
      } else if (item && item.children) {
        return { ...item, children: manageEmptyChildren(item.children) };
      } else {
        return item;
      }
    });

  return normalizedChildren;
};
